<script setup lang="ts">
import { computed, ref } from 'vue'

import {
  ACCEPTED_ATTACHMENT,
  attachmentIcon,
  type OrpheusAttachment,
} from '../composables/useOrpheusFileAttachment'

import { OrpheusCreditHint } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { Button, Icon, Textarea } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    locked?: boolean
    streaming?: boolean
    disabled?: boolean
    showDisclaimer?: boolean
    /** A KPI target file can be attached to the main chat only (v3 `allow-attachments`). */
    allowAttach?: boolean
    attachment?: OrpheusAttachment | null
  }>(),
  {
    locked: false,
    streaming: false,
    disabled: false,
    showDisclaimer: true,
    allowAttach: false,
    attachment: null,
  },
)
const model = defineModel<string>({ default: '' })
const emit = defineEmits<{
  send: [content: string]
  cancel: []
  attach: [file: File]
  'remove-attachment': []
}>()
const { t } = useI18n()
const fileInput = ref<HTMLInputElement | null>(null)

const inputDisabled = computed(() => props.locked || props.disabled || props.streaming)
// A ready attachment can be sent on its own; one still uploading holds the send back.
const sendDisabled = computed(() => {
  if (inputDisabled.value || props.attachment?.status === 'uploading') return true
  return model.value.trim().length === 0 && props.attachment?.status !== 'ready'
})

function send(): void {
  if (sendDisabled.value) return
  emit('send', model.value.trim())
  model.value = ''
}

function pickFile(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) emit('attach', file)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || event.shiftKey) return
  event.preventDefault()
  send()
}
</script>

<template>
  <div
    class="orpheus-composer grid gap-8 w-[calc(100%_-_2rem)] max-w-216"
    :data-streaming="streaming || undefined"
  >
    <OrpheusCreditHint />
    <div
      v-if="attachment"
      class="orpheus-composer__attachment -mb-xs flex flex-wrap items-center gap-2 px-1 text-sm"
      data-testid="orpheus-input-attachment"
    >
      <span
        class="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-primary"
      >
        <Icon
          :name="attachmentIcon(attachment.name)"
          :size="14"
          weight="light"
          aria-hidden="true"
        />
        {{ attachment.name }}
        <span v-if="attachment.status === 'uploading'" class="text-muted">
          {{ t('orpheus.attachment.reading') }}
        </span>
        <span
          v-else-if="attachment.status === 'ready' && attachment.rowCount !== null"
          class="text-muted"
        >
          {{ t('orpheus.attachment.rows', { count: attachment.rowCount }) }}
        </span>
        <button
          type="button"
          class="ml-1 cursor-pointer border-0 bg-transparent p-0 text-muted transition hover:text-primary"
          :aria-label="t('orpheus.attachment.remove')"
          @click="emit('remove-attachment')"
        >
          <Icon name="x" :size="12" weight="regular" aria-hidden="true" />
        </button>
      </span>
      <span
        v-if="attachment.status === 'error'"
        class="text-fg"
        data-testid="orpheus-input-attachment-error"
      >
        {{ attachment.error || t(attachment.errorKey) }}
      </span>
    </div>
    <div
      class="orpheus-composer__input relative rounded-full bg-orpheus-composer p-px shadow-orpheus-composer"
    >
      <div class="flex min-h-12 items-center gap-xs rounded-full bg-surface px-md">
        <Textarea
          v-model="model"
          variant="plain"
          size="xl"
          :rows="1"
          auto-grow
          :disabled="inputDisabled"
          :placeholder="
            t(locked ? 'orpheus.composer.lockedPlaceholder' : 'orpheus.composer.placeholder')
          "
          @keydown="onKeydown"
        />
        <template v-if="allowAttach">
          <input
            ref="fileInput"
            type="file"
            :accept="ACCEPTED_ATTACHMENT"
            class="hidden"
            data-testid="orpheus-input-file"
            @change="pickFile"
          />
          <!-- Grey until hovered, as v3's: a plain button, so the composer's brand tint on its
             send/stop `Button`s does not reach it. -->
          <button
            type="button"
            class="orpheus-composer__attach flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-muted transition hover:bg-orpheus-brand/10 hover:text-orpheus-brand disabled:pointer-events-none disabled:opacity-50"
            :disabled="locked || streaming || disabled"
            :aria-label="t('orpheus.attachment.attach')"
            data-testid="orpheus-input-attach"
            @click="fileInput?.click()"
          >
            <Icon name="paperclip" :size="18" weight="light" aria-hidden="true" />
          </button>
        </template>
        <Button
          v-if="streaming"
          variant="danger"
          class="shrink-0 rounded-full"
          size="lg"
          square
          :aria-label="t('orpheus.composer.stop')"
          @click="emit('cancel')"
        >
          <Icon name="x" :size="18" aria-hidden="true" />
        </Button>
        <Button
          v-else
          size="lg"
          square
          variant="ghost"
          class="shrink-0 rounded-full text-orpheus-brand"
          :disabled="sendDisabled"
          :aria-label="t('orpheus.composer.send')"
          @click="send"
        >
          <Icon
            name="paper-airplane"
            :size="20"
            class="rotate-45 text-orpheus-brand"
            aria-hidden="true"
          />
        </Button>
      </div>
    </div>
    <p
      v-if="showDisclaimer"
      class="orpheus-composer__disclaimer m-0 text-orpheus-caption text-sm text-center"
    >
      {{ t('orpheus.composer.disclaimer') }}
    </p>
  </div>
</template>
